import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../../../../components/ui/button';
import { CoatingDftLayersTable, type CoatingDftLayerRow } from './CoatingChecklistDemoPanels';
import {
  createSeedChecklistSections,
  isCoatingDftHostCategory,
  newCoatingChecklistId,
  type CoatingChecklistTemplateSection,
} from './coatingChecklistMockData';
import { CoatingChecklistTemplateTable } from './CoatingChecklistTemplateTable';
import { cn } from '../../../../../../lib/utils';

const DEFAULT_DFT_LAYERS: CoatingDftLayerRow[] = [
  { id: 'dft-layer-default-a', name: 'First layer' },
  { id: 'dft-layer-default-b', name: 'Final layer' },
];

function cloneDefaultDftLayers(): CoatingDftLayerRow[] {
  return DEFAULT_DFT_LAYERS.map((l) => ({ ...l }));
}

export type CoatingSpoolPersistPayload = {
  checklistSections: CoatingChecklistTemplateSection[];
  checklistValues: Record<string, string>;
};

function normalizeInitialDraft(draft?: Partial<CoatingSpoolPersistPayload> | null): CoatingSpoolPersistPayload {
  const sections =
    draft?.checklistSections && draft.checklistSections.length > 0
      ? JSON.parse(JSON.stringify(draft.checklistSections)) as CoatingChecklistTemplateSection[]
      : createSeedChecklistSections();
  const values =
    draft?.checklistValues && typeof draft.checklistValues === 'object'
      ? { ...draft.checklistValues }
      : {};
  return { checklistSections: sections, checklistValues: values };
}

export interface CoatingSpoolChecklistEditorProps {
  /** When this string changes, editor resets from `initialDraft`. */
  resetKey: string;
  initialDraft?: Partial<CoatingSpoolPersistPayload> | null;
  /** When set, only this checklist category block is shown (full payload still saved). */
  singleSectionId?: string | null;
  canEdit?: boolean;
  onSave: (payload: CoatingSpoolPersistPayload) => void | Promise<void>;
  saveLabel?: string;
  saveButtonClassName?: string;
  titleLabel?: string;
}

export const CoatingSpoolChecklistEditor: React.FC<CoatingSpoolChecklistEditorProps> = ({
  resetKey,
  initialDraft,
  singleSectionId = null,
  canEdit = true,
  onSave,
  saveLabel = 'Save checklist',
  saveButtonClassName,
  titleLabel = 'Coating checklist',
}) => {
  const [cellValues, setCellValues] = useState<Record<string, string>>({});
  const [checklistSections, setChecklistSections] = useState<CoatingChecklistTemplateSection[]>([]);
  const [dftLayers, setDftLayers] = useState<CoatingDftLayerRow[]>(() => cloneDefaultDftLayers());
  const [saving, setSaving] = useState(false);

  const checklistSectionsRef = React.useRef(checklistSections);
  checklistSectionsRef.current = checklistSections;

  useEffect(() => {
    const n = normalizeInitialDraft(initialDraft);
    setChecklistSections(n.checklistSections);
    setCellValues(n.checklistValues);
    setDftLayers(cloneDefaultDftLayers());
    // Intentionally only when scoped identity changes — avoids wiping edits when parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const patchCell = useCallback((key: string, value: string) => {
    if (!canEdit) return;
    setCellValues((prev) => ({ ...prev, [key]: value }));
  }, [canEdit]);

  const handleLayerNameChange = useCallback(
    (layerId: string, name: string) => {
      if (!canEdit) return;
      setDftLayers((rows) => rows.map((r) => (r.id === layerId ? { ...r, name } : r)));
    },
    [canEdit],
  );

  const handleAddDftLayer = useCallback(() => {
    if (!canEdit) return;
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? `dft-${crypto.randomUUID()}`
        : `dft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setDftLayers((rows) => [...rows, { id, name: `Layer ${rows.length + 1}` }]);
  }, [canEdit]);

  const handleRemoveDftLayer = useCallback(
    (layerId: string) => {
      if (!canEdit) return;
      const prefix = `dft::${layerId}::`;
      setDftLayers((rows) => {
        if (rows.length <= 1) return rows;
        return rows.filter((r) => r.id !== layerId);
      });
      setCellValues((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.startsWith(prefix)) delete next[k];
        }
        return next;
      });
    },
    [canEdit],
  );

  const handleUpdateSectionCategory = useCallback(
    (sectionId: string, category: string) => {
      if (!canEdit) return;
      setChecklistSections((sections) =>
        sections.map((s) => (s.id === sectionId ? { ...s, category } : s)),
      );
    },
    [canEdit],
  );

  const handleRemoveSection = useCallback(
    (sectionId: string) => {
      if (!canEdit || singleSectionId) return;
      const sections = checklistSectionsRef.current;
      const sec = sections.find((s) => s.id === sectionId);
      if (!sec || sections.length <= 1) return;

      const prefixes = sec.items.map((it) => `chk::${it.id}::`);
      setCellValues((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (prefixes.some((p) => k.startsWith(p))) delete next[k];
        }
        return next;
      });

      setChecklistSections((prev) => prev.filter((s) => s.id !== sectionId));
    },
    [canEdit, singleSectionId],
  );

  const handleRemoveItem = useCallback(
    (sectionId: string, itemId: string) => {
      if (!canEdit) return;
      const sections = checklistSectionsRef.current;
      const sec = sections.find((s) => s.id === sectionId);
      if (!sec || sec.items.length <= 1) return;

      const prefix = `chk::${itemId}::`;
      setCellValues((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.startsWith(prefix)) delete next[k];
        }
        return next;
      });

      setChecklistSections((prev) =>
        prev.map((s) =>
          s.id !== sectionId
            ? s
            : { ...s, items: s.items.filter((it) => it.id !== itemId) },
        ),
      );
    },
    [canEdit],
  );

  const handleAddItem = useCallback(
    (sectionId: string) => {
      if (!canEdit) return;
      const newId = newCoatingChecklistId('chk');
      setChecklistSections((prev) =>
        prev.map((s) =>
          s.id !== sectionId
            ? s
            : {
                ...s,
              items: [...s.items, { id: newId, description: '', criteria: 'Accept/Reject/NA', value: 'N/A' }],
              },
        ),
      );
    },
    [canEdit],
  );

  const handleUpdateItemDescription = useCallback(
    (sectionId: string, itemId: string, description: string) => {
      if (!canEdit) return;
      setChecklistSections((sections) =>
        sections.map((s) =>
          s.id !== sectionId
            ? s
            : {
                ...s,
                items: s.items.map((it) => (it.id !== itemId ? it : { ...it, description })),
              },
        ),
      );
    },
    [canEdit],
  );

  const handleUpdateItemValue = useCallback(
    (sectionId: string, itemId: string, field: string, value: unknown) => {
      if (!canEdit) return;
      setChecklistSections((sections) =>
        sections.map((s) =>
          s.id !== sectionId
            ? s
            : {
                ...s,
                items: s.items.map((it) => (it.id !== itemId ? it : { ...it, [field]: value })),
              },
        ),
      );
    },
    [canEdit],
  );

  const handleSaveClick = useCallback(async () => {
    setSaving(true);
    try {
      await onSave({
        checklistSections,
        checklistValues: cellValues,
      });
    } finally {
      setSaving(false);
    }
  }, [checklistSections, cellValues, onSave]);

  const tableSections = singleSectionId
    ? checklistSections.filter((s) => s.id === singleSectionId)
    : checklistSections;
  const singleCategoryLabel =
    singleSectionId != null && singleSectionId !== ''
      ? checklistSections.find((s) => s.id === singleSectionId)?.category
      : null;

  const renderInspectionDftFooter = useCallback(
    (section: CoatingChecklistTemplateSection) => {
      if (!isCoatingDftHostCategory(section.category)) return null;
      return (
        <div className="border-t border-slate-200 bg-slate-50/60 p-3">
          <CoatingDftLayersTable
            layers={dftLayers}
            values={cellValues}
            onChange={patchCell}
            onLayerNameChange={handleLayerNameChange}
            onAddLayer={handleAddDftLayer}
            onRemoveLayer={handleRemoveDftLayer}
          />
        </div>
      );
    },
    [cellValues, dftLayers, handleAddDftLayer, handleLayerNameChange, handleRemoveDftLayer, patchCell],
  );

  return (
    <div className="space-y-4">
      {checklistSections.length > 0 ? (
        <>
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-800">
              {singleCategoryLabel?.trim() ? singleCategoryLabel : titleLabel}
            </h3>
            <CoatingChecklistTemplateTable
              sections={tableSections}
              onUpdateSectionCategory={handleUpdateSectionCategory}
              onRemoveSection={handleRemoveSection}
              onRemoveItem={handleRemoveItem}
              onUpdateItemDescription={handleUpdateItemDescription}
              onUpdateItemField={canEdit ? handleUpdateItemValue : undefined}
              onAddItem={canEdit ? handleAddItem : undefined}
              sectionFooter={renderInspectionDftFooter}
              hideCategoryHeader
            />
          </div>
        </>
      ) : null}

      {canEdit ? (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            className={cn('bg-cyan-700 text-white hover:bg-cyan-700/90', saveButtonClassName)}
            disabled={saving}
            onClick={() => void handleSaveClick()}
          >
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
